// "Counters & Limits" as one table (ENG2-2461 / T8 of ENG2-2452).
//
// Every row shows the same columns — Cap (editable) / Used / Remaining /
// Progress / Resets — derived by usage-model.ts, so Sales and CS no longer
// have to know which field is a cap, which is a remaining balance and which
// is a live count. Caps of INT max render as "Unlimited"; counters that track
// nothing render as "Not tracked" with a tooltip that says why.

import * as React from 'react'
import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SyncProblemOutlinedIcon from '@mui/icons-material/SyncProblemOutlined'
import numeral from 'numeral'
import { ACTIVE_SUBSCRIPTION_PLANS } from 'types'
import { usageBand, USAGE_BAND_COLOUR } from 'utils/usage'
import InlineCapEditor, { formatCap } from './inline-cap-editor'
import { buildUsageRow, UsageInput, UsageRow } from './usage-model'
import { NordicPeriod, formatPeriod } from '../nordic-contract-year'
import {
  LEGEND,
  UNTRACKED_REASONS,
  USAGE_GROUPS,
  USAGE_ROWS,
} from './usage-rows'

export type UsageTableProps = UsageInput & {
  search: string
  isSaving: boolean
  onSaveCap: (key: string, value: number) => void
  // ENG2-2461: the current Nordic lookup contract year, shown under the
  // Nordic lookups row.
  nordicPeriod?: NordicPeriod | null
}

const BAND_COLOUR = USAGE_BAND_COLOUR

const formatNumber = (value: number) => numeral(value).format('0,0')

const RESYNC_HINT =
  'The cap was lowered below what the counter still holds; it re-syncs on the next reset.'

const headerCellSx = {
  py: 1,
  px: 1.5,
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap' as const,
}

const cellSx = {
  py: 1.25,
  px: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap' as const,
}

const groupTitleSx = {
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  fontSize: '0.7rem',
  letterSpacing: '0.5px',
}

// "0%" would be a lie for 20,000 requests against a large cap; anything used
// but under one percent reads "<1%".
const percentLabel = (row: UsageRow): string =>
  row.percent === 0 && (row.used ?? 0) > 0 ? '<1%' : `${row.percent}%`

function Progress({ row }: { row: UsageRow }) {
  if (row.percent === null) {
    // No cap to measure against (unset or zero): nothing to draw.
    return null
  }
  const band = usageBand(row.percent)
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minWidth: 140 }}
    >
      <LinearProgress
        variant="determinate"
        value={Math.min(row.percent, 100)}
        aria-label={`${row.label} usage ${row.percent}%`}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { backgroundColor: BAND_COLOUR[band] },
        }}
      />
      <Typography
        variant="caption"
        sx={{ width: 40, textAlign: 'right', color: 'text.secondary' }}
      >
        {percentLabel(row)}
      </Typography>
    </Stack>
  )
}

function NotTracked({ row }: { row: UsageRow }) {
  const reason = row.untrackedReason
    ? UNTRACKED_REASONS[row.untrackedReason]
    : ''
  return (
    <Tooltip title={reason} placement="top">
      <Typography
        component="span"
        variant="caption"
        aria-label={reason || undefined}
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          fontStyle: 'italic',
          cursor: reason ? 'help' : 'default',
        }}
      >
        Not tracked
      </Typography>
    </Tooltip>
  )
}

function ItemCell({ row, note }: { row: UsageRow; note?: string | null }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.label}
        </Typography>
        {row.tooltip && (
          <Tooltip title={row.tooltip} placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }}
              aria-label={`${row.label} info`}
            />
          </Tooltip>
        )}
      </Stack>
      {note && (
        <Typography variant="caption" color="text.secondary" display="block">
          {note}
        </Typography>
      )}
    </Box>
  )
}

const rowNote = (row: UsageRow, nordicPeriod?: NordicPeriod | null) =>
  row.key === 'lookups' && nordicPeriod
    ? `Current period: ${formatPeriod(nordicPeriod)}`
    : null

function UsedCell({ row }: { row: UsageRow }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="body2" data-testid="usage-used">
        {formatNumber(row.used ?? 0)}
      </Typography>
      {row.usageNote && (
        <Typography variant="caption" color="text.secondary">
          {row.usageNote}
        </Typography>
      )}
      {row.resync && (
        <Tooltip title={RESYNC_HINT} placement="top">
          <SyncProblemOutlinedIcon
            sx={{ fontSize: 14, color: 'warning.main' }}
            aria-label={RESYNC_HINT}
          />
        </Tooltip>
      )}
    </Stack>
  )
}

function RemainingCell({ row }: { row: UsageRow }) {
  return (
    <Typography variant="body2" data-testid="usage-remaining">
      {formatNumber(row.remaining ?? 0)}
    </Typography>
  )
}

function UsageTableRow({
  row,
  isSaving,
  onSaveCap,
  note,
}: {
  row: UsageRow
  isSaving: boolean
  onSaveCap: UsageTableProps['onSaveCap']
  note?: string | null
}) {
  const cap = (
    <Box data-testid="usage-cap">
      <InlineCapEditor
        label={row.label}
        value={row.cap}
        isLoading={isSaving}
        onSave={(value) => onSaveCap(row.key, value)}
      />
    </Box>
  )

  if (!row.hasUsage) {
    return (
      <TableRow data-testid={`usage-row-${row.key}`} hover>
        <TableCell sx={cellSx}>
          <ItemCell row={row} note={note} />
        </TableCell>
        <TableCell sx={cellSx}>{cap}</TableCell>
        <TableCell sx={cellSx} colSpan={3}>
          <Typography variant="caption" color="text.secondary">
            A size limit, not an allowance
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  if (!row.tracked) {
    return (
      <TableRow data-testid={`usage-row-${row.key}`} hover>
        <TableCell sx={cellSx}>
          <ItemCell row={row} note={note} />
        </TableCell>
        <TableCell sx={cellSx}>{cap}</TableCell>
        <TableCell sx={cellSx} colSpan={3}>
          <NotTracked row={row} />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow data-testid={`usage-row-${row.key}`} hover>
      <TableCell sx={cellSx}>
        <ItemCell row={row} note={note} />
      </TableCell>
      <TableCell sx={cellSx}>{cap}</TableCell>
      <TableCell sx={cellSx}>
        <UsedCell row={row} />
      </TableCell>
      <TableCell sx={cellSx}>
        <RemainingCell row={row} />
      </TableCell>
      <TableCell sx={cellSx}>
        <Progress row={row} />
      </TableCell>
    </TableRow>
  )
}

// Narrow screens: the same values as labelled stacked cards.
function UsageCard({
  row,
  isSaving,
  onSaveCap,
  note,
}: {
  row: UsageRow
  isSaving: boolean
  onSaveCap: UsageTableProps['onSaveCap']
  note?: string | null
}) {
  const Field = ({
    label,
    children,
  }: {
    label: string
    children: React.ReactNode
  }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {children}
    </Box>
  )
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 1.5 }}
      data-testid={`usage-row-${row.key}`}
    >
      <Stack spacing={1}>
        <ItemCell row={row} note={note} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <Field label="Cap">
            <Box data-testid="usage-cap">
              <InlineCapEditor
                label={row.label}
                value={row.cap}
                isLoading={isSaving}
                onSave={(value) => onSaveCap(row.key, value)}
              />
            </Box>
          </Field>
          {row.hasUsage && row.tracked && (
            <>
              <Field label="Used">
                <UsedCell row={row} />
              </Field>
              <Field label="Remaining">
                <RemainingCell row={row} />
              </Field>
            </>
          )}
          {row.hasUsage && !row.tracked && (
            <Field label="Used">
              <NotTracked row={row} />
            </Field>
          )}
        </Box>
        {row.hasUsage && row.tracked && <Progress row={row} />}
      </Stack>
    </Paper>
  )
}

function UsageTable({
  plan,
  usersLength,
  caps,
  counters,
  search,
  isSaving,
  onSaveCap,
  nordicPeriod,
}: UsageTableProps) {
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))
  const isActivePlan = Boolean(
    plan && (ACTIVE_SUBSCRIPTION_PLANS as readonly string[]).includes(plan),
  )
  const needle = search.trim().toLowerCase()

  const rows = USAGE_ROWS.filter(
    (definition) =>
      !(definition.hideForActivePlans && isActivePlan) &&
      definition.label.toLowerCase().includes(needle),
  ).map((definition) =>
    buildUsageRow(definition, { plan, usersLength, caps, counters }),
  )

  if (rows.length === 0) return null

  const groups = USAGE_GROUPS.map((group) => ({
    ...group,
    rows: rows.filter((row) => row.group === group.key),
  })).filter((group) => group.rows.length > 0)

  return (
    <Paper
      variant="outlined"
      sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Counters & Limits
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 720, textAlign: { xs: 'left', md: 'right' } }}
        >
          {LEGEND}
        </Typography>
      </Stack>

      {narrow ? (
        <Stack spacing={2} sx={{ p: 2 }}>
          {groups.map((group) => (
            <Box key={group.key}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, ...groupTitleSx }}
              >
                {group.title}
              </Typography>
              <Stack spacing={1}>
                {group.rows.map((row) => (
                  <UsageCard
                    key={row.key}
                    row={row}
                    isSaving={isSaving}
                    onSaveCap={onSaveCap}
                    note={rowNote(row, nordicPeriod)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Counters and limits">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Item</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 150 }}>
                  Cap
                </TableCell>
                <TableCell sx={headerCellSx}>Used</TableCell>
                <TableCell sx={headerCellSx}>Remaining</TableCell>
                <TableCell sx={headerCellSx}>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <React.Fragment key={group.key}>
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        ...cellSx,
                        pt: 2,
                        pb: 0.75,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={groupTitleSx}
                      >
                        {group.title}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {group.rows.map((row) => (
                    <UsageTableRow
                      key={row.key}
                      row={row}
                      isSaving={isSaving}
                      onSaveCap={onSaveCap}
                      note={rowNote(row, nordicPeriod)}
                    />
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

export default UsageTable
export { formatCap }
